import React, { useState, useEffect } from 'react';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useDarkMode } from '../../context/DarkModeContext';
import { IoCheckmarkCircle, IoEllipseOutline, IoChevronForwardOutline, IoAddOutline } from 'react-icons/io5';
import { motion } from 'framer-motion';
import MappingModal from '../Mapping/MappingModal';
import { RubricCategory, RubricCriterion } from '../../hooks/useRubric';
import { useRubricOperations } from '../../hooks/useRubricOperations';
import { useGoals } from '../../hooks/useGoals';
import { supabase } from '../../utils/supabase';
import { useCareerCards } from '../../hooks/useCareerCards';
import RubricSetupModal from './RubricSetupModal';
import { useSaveEvidenceConfig } from '../../hooks/useEvidenceConfig';
import RubricCategoryCard, { RubricCategoryWithProgress, RubricItem } from './RubricCategoryCard';
import { redistributeWeights, suggestNewCategoryWeight } from '../../utils/rubricWeights';
import { useToast } from '../../context/ToastContext';

interface CompanyRubricTabProps {
  focus: CareerFocusWithMetrics;
  activeRubric: any; // Will use the proper type from useRubric
  rubricLoading: boolean;
  rubricError: Error | null;
  refreshRubric: () => Promise<void>;
  onQuickAdd?: (rubricItemId?: string, category?: string, description?: string) => void;
}


const CompanyRubricTab: React.FC<CompanyRubricTabProps> = ({ 
  focus,
  activeRubric,
  rubricLoading,
  rubricError,
  refreshRubric,
  onQuickAdd
}) => {
  const { darkMode } = useDarkMode();
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [rubricCategories, setRubricCategories] = useState<RubricCategoryWithProgress[]>([]);
  const [mappings, setMappings] = useState<any[]>([]);
  const [evidenceCounts, setEvidenceCounts] = useState<Map<string, { total: number; careerCards: number; notes: number }>>(new Map());
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCriterionId, setEditingCriterionId] = useState<string | null>(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategoryWeight, setNewCategoryWeight] = useState(0);
  
  // Get operations without re-fetching data
  const { 
    updateCategory,
    deleteCategory,
    addCategory,
    updateCriterion,
    deleteCriterion,
    addCriterion,
    reorderCategories
  } = useRubricOperations(refreshRubric);
  const { goals } = useGoals(focus.id);
  const { cards } = useCareerCards();
  const { updateCategoryTarget } = useSaveEvidenceConfig();

  const { showToast } = useToast();
  
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const completedColor = darkMode ? 'text-green-400' : 'text-green-600';
  const incompleteColor = darkMode ? 'text-gray-500' : 'text-gray-400';
  const inputBg = darkMode ? 'bg-gray-900' : 'bg-white';

  // Fetch mappings and evidence counts
  useEffect(() => {
    if (!focus.id) return;

    const fetchMappings = async () => {
      const { data } = await supabase
        .from('goal_rubric_mappings')
        .select('*')
        .eq('focus_id', focus.id);
      
      setMappings(data || []);
    };

    const fetchEvidenceCounts = async () => {
      // Fetch evidence linked to rubric items (both old and new formats)
      const { data, error } = await supabase
        .from('content_links')
        .select('target_id, target_type, metadata, source_type')
        .eq('target_type', 'rubric_criterion')
        .in('source_type', ['career_card', 'note']);
      
      if (error) {
        console.error('Error fetching evidence counts:', error);
        return;
      }
      
      const counts = new Map<string, { total: number; careerCards: number; notes: number }>();
      
      // Get normalized criteria mapping
      let criteriaMap: Map<string, string> = new Map();
      if (activeRubric) {
        const { data: criteria } = await supabase
          .from('rubric_criteria')
          .select('id, category_id, display_order')
          .in('category_id', activeRubric.categories.map(c => c.id));
          
        criteria?.forEach(c => {
          criteriaMap.set(c.id, `${c.category_id}-${c.display_order}`);
        });
      }
      
      data?.forEach(link => {
        // Only handle rubric_criterion links (migration complete)
        if (link.target_type === 'rubric_criterion') {
          const key = criteriaMap.get(link.target_id);
          
          if (key) {
            if (!counts.has(key)) {
              counts.set(key, { total: 0, careerCards: 0, notes: 0 });
            }
            const current = counts.get(key)!;
            current.total++;
            if (link.source_type === 'career_card') {
              current.careerCards++;
            } else if (link.source_type === 'note') {
              current.notes++;
            }
          }
        }
      });
      setEvidenceCounts(counts);
    };

    fetchMappings();
    fetchEvidenceCounts();
  }, [focus.id, activeRubric]);

  // Process rubric data with mappings and evidence
  useEffect(() => {
    if (!activeRubric) {
      setRubricCategories([]);
      return;
    }

    // Transform real rubric data
    const categories = activeRubric.categories.map(cat => {
      let items: RubricItem[];
      
      // Use normalized criteria if available
      if (cat.criteria_items && cat.criteria_items.length > 0) {
        items = cat.criteria_items.map((criterion) => {
          const itemId = `${cat.id}-${criterion.display_order}`;
          const counts = evidenceCounts.get(itemId);
          const hasEvidence = counts && counts.total > 0;
          return {
            id: criterion.id,
            text: criterion.text,
            hasEvidence: !!hasEvidence,
            careerCardCount: counts?.careerCards || 0,
            noteCount: counts?.notes || 0
          };
        });
      } else {
        // Fall back to legacy structure
        items = cat.criteria.map((criterion: any, index: number) => {
          const itemId = `${cat.id}-${index}`;
          const counts = evidenceCounts.get(itemId);
          const hasEvidence = counts && counts.total > 0;
          return {
            id: itemId,
            text: typeof criterion === 'string' ? criterion : criterion.text || '',
            hasEvidence: !!hasEvidence,
            careerCardCount: counts?.careerCards || 0,
            noteCount: counts?.notes || 0
          };
        });
      }

      // Find mapped goals for this category
      const categoryMappings = mappings.filter(m => m.rubric_category === cat.id);
      const mappedGoalIds = categoryMappings.map(m => m.goal_id);
      const mappedGoalNames = goals?.filter(g => mappedGoalIds.includes(g.id))
        .map(g => g.goal_text) || [];

      return {
        id: cat.id,
        name: cat.name,
        description: cat.description,
        items,
        mappedGoals: mappedGoalNames,
        completedCount: items.filter(item => item.hasEvidence).length
      };
    });

    setRubricCategories(categories);
  }, [activeRubric, mappings, goals, evidenceCounts]);

  const getCategoryProgress = (category: RubricCategoryWithProgress) => {
    return { completed: category.completedCount, total: category.items.length };
  };

  // Category management handlers
  const handleCategoryUpdate = async (categoryId: string, updates: Partial<RubricCategory>) => {
    if (!activeRubric) return false;

    let result = false;
    // If weight changed, redistribute
    if (updates.weight !== undefined) {
      const updatedCategories = activeRubric.categories.map(cat => 
        cat.id === categoryId ? { ...cat, weight: updates.weight! } : cat
      );
      const redistributed = redistributeWeights(updatedCategories, categoryId);
      
      // Update all categories with new weights
      const promises = redistributed.map(cat => 
        updateCategory(cat.id, { weight: cat.weight })
      );
      const results = await Promise.all(promises);
      result = results.every(r => r);
    } else {
      result = await updateCategory(categoryId, updates);
    }

    if (result) {
      await refreshRubric();
    }
    return result;
  };

  const handleCategoryDelete = async (categoryId: string) => {
    if (!activeRubric) return false;

    // Prevent deleting last category
    if (activeRubric.categories.length === 1) {
      showToast('Cannot delete the last category', 'error');
      return false;
    }

    const result = await deleteCategory(categoryId);
    
    if (result) {
      // Redistribute weights among remaining categories
      const remaining = activeRubric.categories.filter(c => c.id !== categoryId);
      const redistributed = redistributeWeights(remaining);
      
      const promises = redistributed.map(cat => 
        updateCategory(cat.id, { weight: cat.weight })
      );
      await Promise.all(promises);
      await refreshRubric();
    }
    
    return result;
  };

  const handleCategoryReorder = async (index: number, direction: 'up' | 'down') => {
    if (!activeRubric) return;

    const newCategories = [...activeRubric.categories];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newCategories.length) return;
    
    // Swap categories
    [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
    
    // Update order
    const categoryIds = newCategories.map(c => c.id);
    await reorderCategories(categoryIds);
  };

  const handleAddCategory = async () => {
    if (!activeRubric || !newCategoryName.trim()) return;

    const suggestedWeight = suggestNewCategoryWeight(activeRubric.categories);
    
    const newCategory = await addCategory(activeRubric.id, {
      name: newCategoryName.trim(),
      description: newCategoryDescription.trim(),
      weight: newCategoryWeight || suggestedWeight,
      display_order: activeRubric.categories.length,
      criteria: [],
      criteria_items: []
    });

    if (newCategory) {
      setNewCategoryName('');
      setNewCategoryDescription('');
      setNewCategoryWeight(0);
      setIsAddingCategory(false);
      
      // Redistribute weights
      const allCategories = [...activeRubric.categories, newCategory];
      const redistributed = redistributeWeights(allCategories);
      
      const promises = redistributed.map(cat => 
        updateCategory(cat.id, { weight: cat.weight })
      );
      await Promise.all(promises);
      await refreshRubric();
    }
  };

  // Criterion management handlers
  const handleCriterionAdd = async (categoryId: string, text: string, description?: string) => {
    const category = activeRubric?.categories.find(c => c.id === categoryId);
    if (!category) return false;

    const existingCount = category.criteria_items?.length || category.criteria.length || 0;
    
    const result = await addCriterion(categoryId, {
      text,
      description,
      display_order: existingCount
    });

    if (result) {
      await refreshRubric();
    }
    return !!result;
  };

  const handleCriterionUpdate = async (criterionId: string, updates: Partial<RubricCriterion>) => {
    const result = await updateCriterion(criterionId, updates);
    if (result) {
      await refreshRubric();
    }
    return result;
  };

  const handleCriterionDelete = async (criterionId: string) => {
    const result = await deleteCriterion(criterionId);
    if (result) {
      await refreshRubric();
    }
    return result;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-6">
        <h3 className={`text-lg font-semibold ${textColor}`}>Company Evaluation Rubric</h3>
        <p className={`text-sm ${mutedTextColor} mt-1`}>
          How your company will evaluate your performance
        </p>
      </div>

      {/* Loading State */}
      {rubricLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {rubricError && (
        <div className="flex items-center justify-center h-32">
          <div className="text-red-500 text-center">
            <p className="mb-2">Failed to load rubric</p>
            <button 
              onClick={() => refreshRubric()}
              className="text-sm text-blue-500 hover:text-blue-600"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* No Rubric State */}
      {!rubricLoading && !rubricError && !activeRubric && rubricCategories.length === 0 && (
        <div className={`text-center py-12 ${cardBg} rounded-lg border ${borderColor}`}>
          <div className="text-4xl mb-4">📋</div>
          <h3 className={`text-lg font-semibold ${textColor} mb-2`}>
            No Evaluation Rubric Set Up
          </h3>
          <p className={`${mutedTextColor} mb-6 max-w-md mx-auto`}>
            Set up your evaluation rubric to track how your performance will be measured
          </p>
          <button
            onClick={() => setShowSetupModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
          >
            Set Up Rubric
          </button>
        </div>
      )}

      {/* Rubric Categories */}
      {!rubricLoading && !rubricError && activeRubric && (
        <div className="space-y-4">
          {rubricCategories.map((category, index) => (
            <RubricCategoryCard
              key={category.id}
              category={category}
              onUpdate={(updates) => handleCategoryUpdate(category.id, updates)}
              onDelete={() => handleCategoryDelete(category.id)}
              onReorder={(direction) => handleCategoryReorder(index, direction)}
              onAddCriterion={(text, description) => handleCriterionAdd(category.id, text, description)}
              onUpdateCriterion={handleCriterionUpdate}
              onDeleteCriterion={handleCriterionDelete}
              onUpdateEvidenceTarget={updateCategoryTarget}
              isFirst={index === 0}
              isLast={index === rubricCategories.length - 1}
              isEditingAny={!!editingCategoryId || !!editingCriterionId || isAddingCategory}
              onEditStart={() => setEditingCategoryId(category.id)}
              onEditEnd={() => setEditingCategoryId(null)}
              onQuickAdd={onQuickAdd}
            />
          ))}

          {/* Add Category Form */}
          {isAddingCategory ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`p-4 rounded-lg border ${borderColor} ${cardBg} space-y-3`}
            >
              <h4 className={`font-medium ${textColor}`}>Add New Category</h4>
              <input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Category name"
                className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                autoFocus
              />
              <textarea
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                placeholder="Description (optional)"
                className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} resize-none focus:outline-none focus:ring-2 focus:ring-blue-500`}
                rows={2}
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={newCategoryWeight}
                  onChange={(e) => setNewCategoryWeight(Math.max(0, Math.min(100, Number(e.target.value))))}
                  placeholder={`Suggested: ${suggestNewCategoryWeight(activeRubric.categories)}%`}
                  className={`w-24 px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  min="0"
                  max="100"
                />
                <span className={textColor}>% weight</span>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setIsAddingCategory(false);
                    setNewCategoryName('');
                    setNewCategoryDescription('');
                    setNewCategoryWeight(0);
                  }}
                  className={`px-3 py-1 rounded text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCategory}
                  disabled={!newCategoryName.trim()}
                  className={`px-3 py-1 rounded text-sm ${
                    newCategoryName.trim()
                      ? 'bg-blue-500 hover:bg-blue-600 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Add Category
                </button>
              </div>
            </motion.div>
          ) : (
            <button
              onClick={() => setIsAddingCategory(true)}
              className={`w-full p-4 rounded-lg border-2 border-dashed ${borderColor} ${mutedTextColor} hover:border-blue-500 hover:text-blue-500 transition-colors text-sm font-medium flex items-center justify-center gap-2`}
            >
              <IoAddOutline className="w-5 h-5" />
              Add Category
            </button>
          )}
        </div>
      )}

      {/* Edit Mapping Button */}
      <div className="mt-6 text-center">
        <button
          onClick={() => setShowMappingModal(true)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} ${textColor} transition-colors text-sm font-medium`}
        >
          <span>🔗</span>
          <span>Edit Goal-Rubric Mapping</span>
        </button>
      </div>

      {/* Help Text */}
      <div className={`text-sm ${mutedTextColor} mt-6 p-4 ${cardBg} rounded-lg border ${borderColor}`}>
        <p className="font-medium mb-1">💡 Pro tip</p>
        <p>
          Each checkmark represents evidence you've documented. Focus on the uncompleted items to improve your evaluation.
        </p>
      </div>

      {/* Goal-Rubric Mapping Modal */}
      <MappingModal
        focusId={focus.id}
        isOpen={showMappingModal}
        onClose={() => setShowMappingModal(false)}
      />

      {/* Rubric Setup Modal */}
      <RubricSetupModal
        focusId={focus.id}
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onComplete={() => {
          setShowSetupModal(false);
          refreshRubric();
        }}
      />
    </div>
  );
};

export default CompanyRubricTab;